import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { toast } from 'react-toastify'

import {
    useOccupancyAssign,
    useOccupancyConfirm,
} from '../../../hooks/useRenewalOffers'
import { OccupancyBoardFilter, OccupancyLease, OccupancyUnit } from './types'
import { OccupancyFactIcons } from './OccupancyFactIcons'
import { OccupancyUnitReview } from './OccupancyUnitReview'
import { OccupancyUnassignedPool } from './OccupancyUnassignedPool'
import { OccupancyAssignDialog } from './OccupancyAssignDialog'
import { OccupancyLifestyleStrip } from './OccupancyLifestyleStrip'
import { OccupancyMatchScore } from './OccupancyMatchScore'
import { unitMatchScore } from './occupancyMatchScoreUtils'
import { NextActionTableIcon } from '../components/NextActionMark'
import {
    isOccupancyImmovable,
    isUnassignedOccupancyLease,
    resolveOccupancyNextAction,
    unitIsChanged,
} from './occupancyLease'

const OccupancyBoardActions = React.createContext<{
    onMoveTo: (lease: OccupancyLease) => void
} | null>(null)

const FILTER_LABELS: Record<OccupancyBoardFilter, string> = {
    incomplete: 'Incomplete',
    drafts: 'Drafts',
    fillable: 'Fillable',
    conflicts: 'Conflicts',
    complete: 'Complete',
    all: 'All units',
}

const useStyles = makeStyles((theme: Theme) => ({
    board: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    sticky: {
        position: 'sticky',
        top: 0,
        zIndex: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        paddingBottom: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        boxShadow: `0 8px 8px -8px ${theme.palette.divider}`,
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    search: {
        flex: '1 1 220px',
        minWidth: 220,
    },
    group: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
    },
    groupHead: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        padding: theme.spacing(0.75, 1.25),
        backgroundColor: theme.palette.grey[50],
        border: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        color: 'inherit',
    },
    unitList: {
        display: 'flex',
        flexDirection: 'column',
    },
    unitRow: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    unitRowOpen: {
        backgroundColor: '#fffbeb',
    },
    unitLine: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minHeight: 40,
        padding: theme.spacing(0.25, 0.5, 0.25, 0),
    },
    unitName: {
        width: 88,
        flexShrink: 0,
        fontWeight: 600,
        fontSize: '0.8125rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fillCount: {
        width: 48,
        flexShrink: 0,
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontVariantNumeric: 'tabular-nums',
    },
    fillCountOpen: {
        color: '#b45309',
        fontWeight: 700,
    },
    slots: {
        display: 'flex',
        gap: 6,
        flex: 1,
        minWidth: 0,
        paddingRight: theme.spacing(1),
    },
    slot: {
        flex: '1 1 0',
        maxWidth: 336,
        minWidth: 144,
        height: 32,
        borderRadius: 6,
        border: `1px solid ${theme.palette.divider}`,
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fff',
        overflow: 'hidden',
    },
    slotEmpty: {
        borderStyle: 'dashed',
        borderColor: '#f59e0b',
        backgroundColor: '#fffbeb',
        color: '#b45309',
        fontSize: '0.6875rem',
        fontWeight: 700,
        justifyContent: 'center',
    },
    slotOver: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#eef2ff',
        borderStyle: 'solid',
    },
    compactTenant: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        minWidth: 0,
        width: '100%',
        minHeight: 28,
        height: 32,
        borderRadius: 4,
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        padding: '0 2px 0 6px',
    },
    dragHandle: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        flex: 1,
    },
    menuButton: {
        padding: 2,
        flexShrink: 0,
        color: theme.palette.text.secondary,
    },
    compactName: {
        fontSize: '0.75rem',
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    pip: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },
    beds: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: theme.spacing(1),
    },
    bed: {
        minHeight: 84,
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1),
        backgroundColor: '#fff',
    },
    bedOver: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#eef2ff',
    },
    bedEmpty: {
        borderStyle: 'dashed',
        borderColor: '#f59e0b',
        backgroundColor: '#fffbeb',
        color: '#b45309',
        fontWeight: 700,
    },
    suggested: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 20,
    },
    confirmed: {
        backgroundColor: '#d1fae5',
        color: '#047857',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 20,
    },
    changedChip: {
        backgroundColor: '#ffedd5',
        color: '#c2410c',
        fontWeight: 700,
        fontSize: '0.625rem',
        height: 20,
        flexShrink: 0,
        marginRight: theme.spacing(1),
    },
}))

function statusPip(assignment: OccupancyLease['occupancy_assignment']) {
    if (assignment === 'suggested') return '#d97706'
    if (assignment === 'confirmed') return '#047857'
    return '#9ca3af'
}

function haystack(
    unit: OccupancyUnit,
    occupants: Array<OccupancyLease | null>,
) {
    const parts = [
        unit.name,
        unit.unit_config_name || '',
        ...unit.areas.map(
            (area) => `${area.area_config_name} ${area.area_label}`,
        ),
    ]
    for (const lease of occupants) {
        if (!lease) continue
        parts.push(lease.tenant_name, lease.tenant_email)
        const facts = lease.occupancy_facts
        if (!facts) continue
        if (facts.gender) parts.push(facts.gender)
        for (const row of facts.requested || []) {
            parts.push(row.name, row.email, row.phone)
        }
        for (const peer of facts.mutual_with || []) {
            parts.push(peer.name)
        }
        for (const pet of facts.pets || []) {
            parts.push(pet.name, pet.animal_type, pet.breed || '')
        }
    }
    return parts.join(' ').toLowerCase()
}

function requestedSearchText(lease: OccupancyLease) {
    const facts = lease.occupancy_facts
    if (!facts) return ''
    return [
        ...(facts.requested || []).map(
            (row) => `${row.name} ${row.email} ${row.phone}`,
        ),
        ...(facts.mutual_with || []).map((peer) => peer.name),
    ]
        .join(' ')
        .toLowerCase()
}

function OccupancyTenantMenu({ lease }: { lease: OccupancyLease }) {
    const classes = useStyles()
    const assign = useOccupancyAssign()
    const boardActions = React.useContext(OccupancyBoardActions)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const stop = (event: React.SyntheticEvent) => {
        event.stopPropagation()
    }

    return (
        <>
            <IconButton
                className={classes.menuButton}
                size="small"
                aria-label={`Actions for ${lease.tenant_name}`}
                onPointerDown={stop}
                onMouseDown={stop}
                onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    setAnchorEl(event.currentTarget)
                }}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                onClick={stop}
                onPointerDown={stop}
                onMouseDown={stop}
            >
                <MenuItem
                    onClick={() => {
                        setAnchorEl(null)
                        boardActions?.onMoveTo(lease)
                    }}
                >
                    Move to…
                </MenuItem>
                <MenuItem
                    disabled={assign.isLoading}
                    onClick={() => {
                        setAnchorEl(null)
                        assign.mutate({ lease: lease.id, area: null })
                    }}
                >
                    Remove from unit
                </MenuItem>
            </Menu>
        </>
    )
}

function TenantChip({
    lease,
    disabled,
    pool,
}: {
    lease: OccupancyLease
    disabled?: boolean
    pool?: boolean
}) {
    const classes = useStyles()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useDraggable({
        id: `lease-${lease.id}`,
        data: { leaseId: lease.id },
        disabled,
    })
    const showRemove = lease.area_id != null && !isOccupancyImmovable(lease)
    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Translate.toString(transform),
                opacity: isDragging ? 0.6 : 1,
                padding: '6px 4px 6px 8px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                backgroundColor: '#fff',
                width: pool ? '100%' : undefined,
                boxSizing: 'border-box',
            }}
        >
            <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                <Box
                    className={classes.dragHandle}
                    {...listeners}
                    {...attributes}
                    style={{
                        cursor: disabled ? 'default' : 'grab',
                        flex: pool ? '0 0 168px' : 1,
                        width: pool ? 168 : undefined,
                        maxWidth: pool ? 168 : undefined,
                    }}
                >
                    <Box minWidth={0} flex={1}>
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 6 }}
                        >
                            <NextActionTableIcon
                                action={resolveOccupancyNextAction(lease)}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                {lease.tenant_name}
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            flexWrap="wrap"
                            style={{ gap: 6, marginTop: 4 }}
                        >
                            <OccupancyFactIcons lease={lease} />
                            {lease.occupancy_assignment === 'suggested' && (
                                <Chip
                                    label="Suggested"
                                    size="small"
                                    className={classes.suggested}
                                />
                            )}
                            {lease.occupancy_assignment === 'confirmed' && (
                                <Chip
                                    label="Confirmed"
                                    size="small"
                                    className={classes.confirmed}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
                {pool && <OccupancyLifestyleStrip lease={lease} />}
                {showRemove && <OccupancyTenantMenu lease={lease} />}
            </Box>
        </div>
    )
}

function CompactTenant({
    lease,
    disabled,
}: {
    lease: OccupancyLease
    disabled?: boolean
}) {
    const classes = useStyles()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useDraggable({
        id: `lease-${lease.id}`,
        data: { leaseId: lease.id },
        disabled,
    })
    const showRemove = !isOccupancyImmovable(lease)
    return (
        <div
            ref={setNodeRef}
            className={classes.compactTenant}
            style={{
                transform: CSS.Translate.toString(transform),
                opacity: isDragging ? 0.6 : 1,
            }}
        >
            <Box
                className={classes.dragHandle}
                {...listeners}
                {...attributes}
                style={{ cursor: disabled ? 'default' : 'grab' }}
            >
                <Box
                    className={classes.pip}
                    style={{
                        backgroundColor: statusPip(lease.occupancy_assignment),
                    }}
                />
                <NextActionTableIcon
                    action={resolveOccupancyNextAction(lease)}
                />
                <OccupancyFactIcons lease={lease} compact />
                <Typography className={classes.compactName}>
                    {lease.tenant_name}
                </Typography>
            </Box>
            {showRemove && <OccupancyTenantMenu lease={lease} />}
        </div>
    )
}

function CompactSlot({
    areaId,
    lease,
}: {
    areaId: number
    lease: OccupancyLease | null
}) {
    const classes = useStyles()
    const { setNodeRef, isOver } = useDroppable({
        id: `area-${areaId}`,
        data: { areaId },
    })
    const locked = lease != null && isOccupancyImmovable(lease)
    return (
        <div
            ref={setNodeRef}
            className={`${classes.slot} ${lease ? '' : classes.slotEmpty} ${
                isOver ? classes.slotOver : ''
            }`}
        >
            {lease ? <CompactTenant lease={lease} disabled={locked} /> : 'Open'}
        </div>
    )
}

function BedDrop({
    areaId,
    label,
    lease,
}: {
    areaId: number
    label: string
    lease: OccupancyLease | null
}) {
    const classes = useStyles()
    const { setNodeRef, isOver } = useDroppable({
        id: `area-${areaId}`,
        data: { areaId },
    })
    const locked = lease != null && isOccupancyImmovable(lease)
    return (
        <div
            ref={setNodeRef}
            className={`${classes.bed} ${!lease ? classes.bedEmpty : ''} ${
                isOver ? classes.bedOver : ''
            }`}
        >
            <Typography variant="caption" color="textSecondary" display="block">
                {label}
            </Typography>
            {lease ? (
                <Box mt={0.75}>
                    <TenantChip lease={lease} disabled={locked} />
                </Box>
            ) : (
                <Typography
                    variant="caption"
                    display="block"
                    style={{ fontWeight: 700, marginTop: 6 }}
                >
                    Open
                </Typography>
            )}
        </div>
    )
}

interface OccupancyBedBoardProps {
    leases: OccupancyLease[]
    units: OccupancyUnit[]
    unitsLoading?: boolean
    focusUnitId?: number | null
    focusNonce?: number
    boardFilter: OccupancyBoardFilter
    onBoardFilterChange: (filter: OccupancyBoardFilter) => void
}

export const OccupancyBedBoard: React.FC<OccupancyBedBoardProps> = ({
    leases,
    units,
    unitsLoading,
    focusUnitId,
    focusNonce,
    boardFilter,
    onBoardFilterChange,
}) => {
    const classes = useStyles()
    const confirm = useOccupancyConfirm()
    const assign = useOccupancyAssign()
    const [query, setQuery] = useState('')
    const [expandedUnitId, setExpandedUnitId] = useState<number | null>(null)
    const [moveLease, setMoveLease] = useState<OccupancyLease | null>(null)
    const [overflowEl, setOverflowEl] = useState<null | HTMLElement>(null)
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
        () => new Set(),
    )
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    )

    useEffect(() => {
        if (focusUnitId == null) return
        const unit = units.find((row) => row.id === focusUnitId)
        if (!unit) return
        const groupKey = String(
            unit.unit_config || unit.unit_config_name || 'Unit type',
        )
        setCollapsedGroups((prev) => {
            const next = new Set(prev)
            next.delete(groupKey)
            return next
        })
        setExpandedUnitId(focusUnitId)
        window.setTimeout(() => {
            document
                .getElementById(`occupancy-unit-${focusUnitId}`)
                ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }, 50)
    }, [focusUnitId, focusNonce, units])

    const leaseByArea = useMemo(() => {
        const map = new Map<number, OccupancyLease>()
        for (const lease of leases) {
            if (lease.area_id != null) map.set(lease.area_id, lease)
        }
        return map
    }, [leases])

    const unassigned = useMemo(
        () => leases.filter(isUnassignedOccupancyLease),
        [leases],
    )

    const unassignedByConfig = useMemo(() => {
        const map = new Map<number, number>()
        for (const lease of unassigned) {
            if (lease.unit_config == null) continue
            map.set(lease.unit_config, (map.get(lease.unit_config) || 0) + 1)
        }
        return map
    }, [unassigned])

    const suggestedCount = leases.filter(
        (lease) => lease.occupancy_assignment === 'suggested',
    ).length

    const rows = useMemo(() => {
        return units.map((unit) => {
            const occupants = unit.areas.map(
                (area) => leaseByArea.get(area.id) ?? null,
            )
            let filled = 0
            let suggested = 0
            for (const lease of occupants) {
                if (!lease) continue
                filled += 1
                if (lease.occupancy_assignment === 'suggested') suggested += 1
            }
            const vacant = unit.areas.length - filled
            const waiting = unassignedByConfig.get(unit.unit_config) || 0
            return {
                unit,
                occupants,
                filled,
                vacant,
                suggested,
                waiting,
                changed: unitIsChanged(occupants, unit.areas.length),
                match: unitMatchScore(occupants, leases),
            }
        })
    }, [units, leaseByArea, unassignedByConfig, leases])

    const filterCounts = useMemo(() => {
        const counts: Record<OccupancyBoardFilter, number> = {
            incomplete: 0,
            drafts: 0,
            fillable: 0,
            conflicts: 0,
            complete: 0,
            all: rows.length,
        }
        for (const row of rows) {
            if (row.vacant > 0) counts.incomplete += 1
            if (row.suggested > 0) counts.drafts += 1
            if (row.vacant > 0 && row.waiting > 0) counts.fillable += 1
            if (row.match.kind === 'conflict') counts.conflicts += 1
            if (row.vacant === 0 && row.suggested === 0) counts.complete += 1
        }
        return counts
    }, [rows])

    const visibleRows = useMemo(() => {
        const needle = query.trim().toLowerCase()
        return rows.filter((row) => {
            if (boardFilter === 'incomplete' && row.vacant === 0) return false
            if (boardFilter === 'drafts' && row.suggested === 0) return false
            if (
                boardFilter === 'fillable' &&
                !(row.vacant > 0 && row.waiting > 0)
            ) {
                return false
            }
            if (boardFilter === 'conflicts' && row.match.kind !== 'conflict') {
                return false
            }
            if (
                boardFilter === 'complete' &&
                (row.vacant > 0 || row.suggested > 0)
            ) {
                return false
            }
            if (!needle) return true
            return haystack(row.unit, row.occupants).includes(needle)
        })
    }, [rows, boardFilter, query])

    const groups = useMemo(() => {
        const byKey = new Map<
            string,
            {
                key: string
                label: string
                rows: typeof visibleRows
            }
        >()
        for (const row of visibleRows) {
            const label = row.unit.unit_config_name || 'Unit type'
            const key = String(row.unit.unit_config || label)
            const existing = byKey.get(key)
            if (existing) {
                existing.rows.push(row)
            } else {
                byKey.set(key, { key, label, rows: [row] })
            }
        }
        const grouped = Array.from(byKey.values())
        grouped.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true }),
        )
        for (const group of grouped) {
            group.rows.sort((a, b) =>
                a.unit.name.localeCompare(b.unit.name, undefined, {
                    numeric: true,
                }),
            )
        }
        return grouped
    }, [visibleRows])

    const poolLeases = useMemo(() => {
        const needle = query.trim().toLowerCase()
        if (!needle) return unassigned
        return unassigned.filter((lease) =>
            `${lease.tenant_name} ${lease.tenant_email} ${
                lease.unit_config_name || ''
            } ${requestedSearchText(lease)}`
                .toLowerCase()
                .includes(needle),
        )
    }, [unassigned, query])

    const handleDragEnd = (event: DragEndEvent) => {
        const leaseId = event.active.data.current?.leaseId as number | undefined
        const overId = event.over?.id
        if (leaseId == null || overId == null) return
        if (String(overId) === 'unassigned-pool') {
            const current = leases.find((row) => row.id === leaseId)
            if (current && isOccupancyImmovable(current)) {
                toast.error('Renewals cannot be moved from their bed.')
                return
            }
            assign.mutate({ lease: leaseId, area: null })
            return
        }
        const areaId = event.over?.data.current?.areaId as number | undefined
        if (areaId == null) return
        const current = leases.find((row) => row.id === leaseId)
        if (current && isOccupancyImmovable(current)) {
            toast.error('Renewals cannot be moved from their bed.')
            return
        }
        assign.mutate({ lease: leaseId, area: areaId })
        const unit = units.find((row) =>
            row.areas.some((area) => area.id === areaId),
        )
        if (unit) setExpandedUnitId(unit.id)
    }

    const toggleGroup = (key: string) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    if (unitsLoading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <OccupancyBoardActions.Provider value={{ onMoveTo: setMoveLease }}>
            <Box className={classes.board}>
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                    <Box className={classes.sticky}>
                        <Box className={classes.filterRow}>
                            {(Object.keys(
                                FILTER_LABELS,
                            ) as OccupancyBoardFilter[]).map((key) => (
                                <Chip
                                    key={key}
                                    label={`${FILTER_LABELS[key]} (${filterCounts[key]})`}
                                    size="small"
                                    clickable
                                    color={
                                        boardFilter === key
                                            ? 'primary'
                                            : 'default'
                                    }
                                    onClick={() => onBoardFilterChange(key)}
                                    style={{ fontWeight: 600 }}
                                />
                            ))}
                            <TextField
                                className={classes.search}
                                size="small"
                                variant="outlined"
                                placeholder="Search units or residents"
                                value={query}
                                onChange={(event) =>
                                    setQuery(event.target.value)
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                fontSize="small"
                                                color="disabled"
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box>
                                <IconButton
                                    size="small"
                                    aria-label="More occupancy actions"
                                    onClick={(event) =>
                                        setOverflowEl(event.currentTarget)
                                    }
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                                <Menu
                                    anchorEl={overflowEl}
                                    open={Boolean(overflowEl)}
                                    onClose={() => setOverflowEl(null)}
                                >
                                    <MenuItem
                                        disabled={
                                            confirm.isLoading ||
                                            suggestedCount === 0
                                        }
                                        onClick={() => {
                                            setOverflowEl(null)
                                            confirm.mutate(undefined)
                                        }}
                                    >
                                        {confirm.isLoading
                                            ? 'Confirming…'
                                            : `Confirm all drafts (${suggestedCount})`}
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Box>

                        <OccupancyUnassignedPool
                            leases={poolLeases}
                            renderPerson={(lease) => (
                                <TenantChip
                                    key={lease.id}
                                    lease={lease}
                                    pool
                                    disabled={isOccupancyImmovable(lease)}
                                />
                            )}
                        />
                    </Box>

                    <Typography variant="caption" color="textSecondary">
                        Showing {visibleRows.length} of {rows.length} units
                    </Typography>

                    {groups.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            No units match this filter.
                        </Typography>
                    ) : (
                        groups.map((group) => {
                            const collapsed = collapsedGroups.has(group.key)
                            return (
                                <Box key={group.key} className={classes.group}>
                                    <button
                                        type="button"
                                        className={classes.groupHead}
                                        onClick={() => toggleGroup(group.key)}
                                    >
                                        {collapsed ? (
                                            <KeyboardArrowRightIcon fontSize="small" />
                                        ) : (
                                            <KeyboardArrowDownIcon fontSize="small" />
                                        )}
                                        <Typography variant="subtitle2">
                                            {group.label}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {group.rows.length} unit
                                            {group.rows.length === 1 ? '' : 's'}
                                        </Typography>
                                    </button>
                                    {!collapsed && (
                                        <Box className={classes.unitList}>
                                            {group.rows.map((row) => {
                                                const expanded =
                                                    expandedUnitId ===
                                                    row.unit.id
                                                const hasOpen = row.vacant > 0
                                                return (
                                                    <Box
                                                        key={row.unit.id}
                                                        id={`occupancy-unit-${row.unit.id}`}
                                                        className={`${
                                                            classes.unitRow
                                                        } ${
                                                            hasOpen
                                                                ? classes.unitRowOpen
                                                                : ''
                                                        }`}
                                                    >
                                                        <Box
                                                            className={
                                                                classes.unitLine
                                                            }
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    setExpandedUnitId(
                                                                        expanded
                                                                            ? null
                                                                            : row
                                                                                  .unit
                                                                                  .id,
                                                                    )
                                                                }
                                                            >
                                                                {expanded ? (
                                                                    <KeyboardArrowDownIcon fontSize="small" />
                                                                ) : (
                                                                    <KeyboardArrowRightIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                            <Typography
                                                                className={
                                                                    classes.unitName
                                                                }
                                                            >
                                                                {row.unit.name}
                                                            </Typography>
                                                            <Typography
                                                                className={`${
                                                                    classes.fillCount
                                                                } ${
                                                                    hasOpen
                                                                        ? classes.fillCountOpen
                                                                        : ''
                                                                }`}
                                                            >
                                                                {row.filled}/
                                                                {
                                                                    row.unit
                                                                        .areas
                                                                        .length
                                                                }
                                                            </Typography>
                                                            <OccupancyMatchScore
                                                                result={
                                                                    row.match
                                                                }
                                                            />
                                                            {!expanded && (
                                                                <Box
                                                                    className={
                                                                        classes.slots
                                                                    }
                                                                >
                                                                    {row.unit.areas.map(
                                                                        (
                                                                            area,
                                                                            index,
                                                                        ) => (
                                                                            <CompactSlot
                                                                                key={
                                                                                    area.id
                                                                                }
                                                                                areaId={
                                                                                    area.id
                                                                                }
                                                                                lease={
                                                                                    row
                                                                                        .occupants[
                                                                                        index
                                                                                    ]
                                                                                }
                                                                            />
                                                                        ),
                                                                    )}
                                                                </Box>
                                                            )}
                                                            {expanded && (
                                                                <Box flex={1} />
                                                            )}
                                                            {row.changed && (
                                                                <Chip
                                                                    label="Changed"
                                                                    size="small"
                                                                    className={
                                                                        classes.changedChip
                                                                    }
                                                                />
                                                            )}
                                                        </Box>
                                                        <Collapse
                                                            in={expanded}
                                                            unmountOnExit
                                                        >
                                                            <OccupancyUnitReview
                                                                occupants={
                                                                    row.occupants
                                                                }
                                                                bedCount={
                                                                    row.unit
                                                                        .areas
                                                                        .length
                                                                }
                                                                match={
                                                                    row.match
                                                                }
                                                                leases={leases}
                                                                confirming={
                                                                    confirm.isLoading
                                                                }
                                                                onConfirm={(
                                                                    leaseIds,
                                                                ) =>
                                                                    confirm.mutate(
                                                                        leaseIds,
                                                                    )
                                                                }
                                                            >
                                                                <Box
                                                                    className={
                                                                        classes.beds
                                                                    }
                                                                >
                                                                    {row.unit.areas.map(
                                                                        (
                                                                            area,
                                                                            index,
                                                                        ) => (
                                                                            <BedDrop
                                                                                key={
                                                                                    area.id
                                                                                }
                                                                                areaId={
                                                                                    area.id
                                                                                }
                                                                                label={`${area.area_config_name} ${area.area_label}`}
                                                                                lease={
                                                                                    row
                                                                                        .occupants[
                                                                                        index
                                                                                    ]
                                                                                }
                                                                            />
                                                                        ),
                                                                    )}
                                                                </Box>
                                                            </OccupancyUnitReview>
                                                        </Collapse>
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            )
                        })
                    )}
                </DndContext>
                <OccupancyAssignDialog
                    open={moveLease != null}
                    lease={moveLease}
                    units={units}
                    leases={leases}
                    title="Move to…"
                    onClose={() => setMoveLease(null)}
                />
            </Box>
        </OccupancyBoardActions.Provider>
    )
}
